import type { IntegrationCategory, IntegrationType } from './types'

/**
 * UI-owned display taxonomy for integrations — NOT a wire type.
 *
 * The backend `IntegrationCategory` enum (25 closed values, nullable) is too
 * granular for the mockup's rail/section headers, so this module folds it into
 * a smaller set of **display groups** that both the list page ("across N
 * categories" KPI, plus the row subtitle wherever the group is not "Other" —
 * see `integrationSubtitle`, which suppresses the escape hatch rather than
 * describe one connector as "Other") and the new page (rail + section headers)
 * consume, so they group identically.
 *
 * Option C (ADR 0018): there is no wire signal for a "SIEM" group, so we do NOT
 * invent one by hardcoding product ids — SIEM is deferred to a future backend
 * `SIEM` category / `display_group` field. Every unmapped or `null` category
 * folds into "Other" so a new backend category can never silently vanish from a
 * rail.
 */

/** Display-group labels — the single UI-owned source; slugs derive from these. */
export const DISPLAY_GROUPS = {
	EDR_ENDPOINT: 'EDR / Endpoint',
	IDENTITY_ACCESS: 'Identity & Access',
	EMAIL_SECURITY: 'Email Security',
	CLOUD_SAAS_AUDIT: 'Cloud & SaaS Audit',
	NETWORK_FIREWALL: 'Network & Firewall',
	INFRASTRUCTURE: 'Infrastructure',
	MANAGEMENT_RMM: 'Management & RMM',
	VULN_THREAT_INTEL: 'Vulnerability & Threat Intel',
	AI_LLM_AUDIT: 'AI / LLM Audit',
	OTHER: 'Other',
} as const

/** The closed set of display-group labels — the domain type the map folds into. */
export type DisplayGroup = (typeof DISPLAY_GROUPS)[keyof typeof DISPLAY_GROUPS]

/**
 * Explicit `category → displayGroup` map covering all 25 wire values. Typed as a
 * `Record<IntegrationCategory, DisplayGroup>` so a missing/renamed wire value —
 * or a typo'd group label — fails typecheck; the map can never silently drift
 * from the enum or invent an off-taxonomy group.
 */
const DISPLAY_GROUP_BY_CATEGORY: Record<IntegrationCategory, DisplayGroup> = {
	'EDR': DISPLAY_GROUPS.EDR_ENDPOINT,
	'Identity & Access': DISPLAY_GROUPS.IDENTITY_ACCESS,
	'Privileged Access & Password Mgmt': DISPLAY_GROUPS.IDENTITY_ACCESS,
	'Email Security': DISPLAY_GROUPS.EMAIL_SECURITY,
	'Cloud Platform': DISPLAY_GROUPS.CLOUD_SAAS_AUDIT,
	'SaaS': DISPLAY_GROUPS.CLOUD_SAAS_AUDIT,
	'AI Governance & Audit': DISPLAY_GROUPS.AI_LLM_AUDIT,
	'RMM': DISPLAY_GROUPS.MANAGEMENT_RMM,
	'Vulnerability Management': DISPLAY_GROUPS.VULN_THREAT_INTEL,
	'Firewall / NGFW': DISPLAY_GROUPS.NETWORK_FIREWALL,
	'IDS / IPS': DISPLAY_GROUPS.NETWORK_FIREWALL,
	'Network Access Control (NAC)': DISPLAY_GROUPS.NETWORK_FIREWALL,
	'Network Detection & Monitoring': DISPLAY_GROUPS.NETWORK_FIREWALL,
	'VPN': DISPLAY_GROUPS.NETWORK_FIREWALL,
	'Web Proxy': DISPLAY_GROUPS.NETWORK_FIREWALL,
	'Wireless / Access Points': DISPLAY_GROUPS.NETWORK_FIREWALL,
	'DNS / DDI / IPAM': DISPLAY_GROUPS.INFRASTRUCTURE,
	'Switch/Router': DISPLAY_GROUPS.INFRASTRUCTURE,
	'Load Balancers': DISPLAY_GROUPS.INFRASTRUCTURE,
	'CDN': DISPLAY_GROUPS.INFRASTRUCTURE,
	'Virtualization': DISPLAY_GROUPS.INFRASTRUCTURE,
	'Physical / Peripheral / Facilities': DISPLAY_GROUPS.INFRASTRUCTURE,
	'Storage & Backup': DISPLAY_GROUPS.INFRASTRUCTURE,
	'Log Collection / Forwarding': DISPLAY_GROUPS.INFRASTRUCTURE,
	'Data Security / DLP': DISPLAY_GROUPS.OTHER,
}

/**
 * Fold a wire category into its display group. `null` and any runtime value not
 * in the map (a new backend category the union doesn't yet know) → "Other".
 */
export function displayGroupForCategory(
	category: IntegrationCategory | null | undefined,
): DisplayGroup {
	if (!category) return DISPLAY_GROUPS.OTHER
	return DISPLAY_GROUP_BY_CATEGORY[category] ?? DISPLAY_GROUPS.OTHER
}

/**
 * The display group where it can DESCRIBE one integration, else `null`.
 *
 * "Other" is a bucket, not a description. It exists so a *grouping* surface —
 * the rail, a section header, the "across N categories" KPI — can never
 * silently lose an integration whose category is `null`, unknown to the map, or
 * (for `Data Security / DLP`) placed there for want of a home. On a surface
 * that describes ONE integration, being told it is in the leftovers bucket says
 * nothing, and "Graylog Dev / Other" said less than the name alone. So the
 * describing surfaces — the connected row's subtitle and the add-catalog card's
 * line 2 — withhold it and show whatever else they have.
 *
 * Withholding only: where this returns a group it is the same group
 * `displayGroupForCategory` returns, so the two can never disagree about which
 * bucket an integration is in.
 */
export function displayGroupAsDescription(
	category: IntegrationCategory | null | undefined,
): DisplayGroup | null {
	const group = displayGroupForCategory(category)
	return group === DISPLAY_GROUPS.OTHER ? null : group
}

/**
 * Display groups actually present in a set of integrations, sorted A→Z. Rails
 * and section headers are built from this — never a fixed list — so an empty
 * group never renders. Ordering is alphabetical everywhere (ADR 0018).
 */
export function displayGroupsPresent(
	integrations: ReadonlyArray<{ category: IntegrationCategory | null }>,
): Array<DisplayGroup> {
	const present = new Set<DisplayGroup>()
	for (const { category } of integrations) {
		present.add(displayGroupForCategory(category))
	}
	return [...present].sort((a, b) => a.localeCompare(b))
}

/**
 * URL slug for a display group — a pure function of the label (lowercase,
 * non-alphanumeric runs → `-`, trimmed), not a hand-named map (ADR 0018). So
 * there is no second naming scheme to keep in sync with the labels.
 */
export function slugForGroup(label: string): string {
	return label
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/(^-|-$)/g, '')
}

/**
 * Resolve a URL slug back to a present display group by slugifying each present
 * group and matching. Unknown / no match → `null` (caller treats as "All").
 */
export function groupFromSlug(
	slug: string,
	presentGroups: ReadonlyArray<DisplayGroup>,
): DisplayGroup | null {
	return presentGroups.find((group) => slugForGroup(group) === slug) ?? null
}

/** URL slug for an integration type (Cloud / Network Log tabs). */
export function slugForType(type: IntegrationType): 'cloud' | 'network-log' {
	return type === 'Cloud' ? 'cloud' : 'network-log'
}

/** Resolve a type slug back to the wire `IntegrationType`; unknown → `null`. */
export function typeFromSlug(slug: string): IntegrationType | null {
	if (slug === 'cloud') return 'Cloud'
	if (slug === 'network-log') return 'Network Log'
	return null
}
